// Cave84 — Bottles service (Phase 1 Week 4)
// Spec §4.1 F1.1–F1.6. Critical rule: bottles are NEVER deleted, only status-changed.

import {
  addDoc, collection, deleteField, doc, getDoc, getDocs,
  increment, orderBy, query, serverTimestamp, Timestamp, updateDoc,
  where, writeBatch, type QueryConstraint
} from 'firebase/firestore';
import type { Bottle, BottleStatus, BottleFormat, Currency, FillLevel, LabelCondition, PurchaseSource } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';
import { createTransfer } from './cellarTransfers';

const COLLECTION = 'bottles';

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface BottleCreateInput {
  vintage_id: string;
  wine_id: string;       // needed to update denormalized counters on Wine + Vintage
  format: BottleFormat;
  purchase_lot_id?: string;    // shared across bulk-add siblings
  purchase_date: Date;
  purchase_source: PurchaseSource;
  purchase_source_detail?: string;
  cost_landed: number;
  cost_currency: Currency;
  cost_eur_at_purchase: number;  // caller calculates; use cost_landed if EUR
  current_cellar_id: string;
  current_location_id?: string;
  is_special_occasion: boolean;
  special_occasion_reason?: string;
  fill_level: FillLevel;
  label_condition: LabelCondition;
  capsule_condition: LabelCondition;
  in_OWC: boolean;
  owc_id?: string;
  notes?: string;
  tags?: string[];
  photos?: string[];
}

export type BottleUpdateInput = Partial<Omit<Bottle,
  'id' | 'vintage_id' | 'status' | 'status_changed_date' | 'created_at' | 'updated_at'
>>;

function col() { return typedCollection<Bottle>(COLLECTION); }

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listBottlesByVintage(vintageId: string): Promise<Bottle[]> {
  const snap = await getDocs(
    query(col(), where('vintage_id', '==', vintageId))
  );
  return snap.docs
    .map(d => d.data())
    .sort((a, b) => {
      const ta = typeof (a.purchase_date as { seconds?: number }).seconds === 'number'
        ? (a.purchase_date as { seconds: number }).seconds : 0;
      const tb = typeof (b.purchase_date as { seconds?: number }).seconds === 'number'
        ? (b.purchase_date as { seconds: number }).seconds : 0;
      return tb - ta;
    });
}

/**
 * All bottles across every vintage of a single wine.
 * Bottles carry only `vintage_id`, so we fan out over the wine's vintage IDs.
 * Firestore caps `in` at 10 values per query — chunk and merge.
 */
export async function listBottlesByVintageIds(vintageIds: string[]): Promise<Bottle[]> {
  if (vintageIds.length === 0) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < vintageIds.length; i += 10) {
    chunks.push(vintageIds.slice(i, i + 10));
  }

  const snaps = await Promise.all(
    chunks.map(chunk => getDocs(query(col(), where('vintage_id', 'in', chunk))))
  );

  return snaps
    .flatMap(snap => snap.docs.map(d => d.data()))
    .sort((a, b) => {
      const ta = typeof (a.purchase_date as { seconds?: number }).seconds === 'number'
        ? (a.purchase_date as { seconds: number }).seconds : 0;
      const tb = typeof (b.purchase_date as { seconds?: number }).seconds === 'number'
        ? (b.purchase_date as { seconds: number }).seconds : 0;
      return tb - ta;
    });
}

export async function listBottlesByCellar(
  cellarId: string,
  status: BottleStatus = 'in_cellar'
): Promise<Bottle[]> {
  const constraints: QueryConstraint[] = [
    where('current_cellar_id', '==', cellarId),
    where('status', '==', status),
    orderBy('current_cellar_id', 'asc'),
  ];
  const snap = await getDocs(query(col(), ...constraints));
  return snap.docs.map(d => d.data());
}

export async function getBottle(id: string): Promise<Bottle | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Bottle, 'id'>) };
}

// ---------------------------------------------------------------------------
// Create (single + bulk)
// ---------------------------------------------------------------------------

// Firestore rejects `undefined` values — strip them before any write.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

function buildBottlePayload(input: BottleCreateInput): Omit<Bottle, 'id'> {
  return stripUndefined({
    vintage_id: input.vintage_id,
    format: input.format,
    purchase_lot_id: input.purchase_lot_id,
    // Use the caller-supplied purchase date — NOT serverTimestamp(). A collector
    // backfilling a 2019 purchase must not end up with today's date.
    purchase_date: Timestamp.fromDate(input.purchase_date),
    purchase_source: input.purchase_source,
    purchase_source_detail: input.purchase_source_detail,
    cost_landed: input.cost_landed,
    cost_currency: input.cost_currency,
    cost_eur_at_purchase: input.cost_eur_at_purchase,
    current_cellar_id: input.current_cellar_id,
    current_location_id: input.current_location_id,
    is_special_occasion: input.is_special_occasion,
    special_occasion_reason: input.special_occasion_reason,
    fill_level: input.fill_level,
    label_condition: input.label_condition,
    capsule_condition: input.capsule_condition,
    in_OWC: input.in_OWC,
    owc_id: input.owc_id,
    status: 'in_cellar',
    status_changed_date: serverTimestamp() as Bottle['status_changed_date'],
    tags: input.tags ?? [],
    photos: input.photos ?? [],
    notes: input.notes,
    created_at: serverTimestamp() as Bottle['created_at'],
    updated_at: serverTimestamp() as Bottle['updated_at'],
  });
}

export async function createBottle(input: BottleCreateInput): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), buildBottlePayload(input));
  // Update denormalized counters on Vintage and Wine
  await updateDoc(doc(db, 'vintages', input.vintage_id), { bottle_count: increment(1) });
  await updateDoc(doc(db, 'wines', input.wine_id), { bottle_count: increment(1) });
  return ref.id;
}

/**
 * Bulk add: create qty bottles in the same lot (shared purchase_lot_id).
 * Returns all created bottle IDs. Counter increments are batched (single write per parent).
 */
export async function createBottles(input: BottleCreateInput, qty: number): Promise<string[]> {
  const lotId = input.purchase_lot_id ?? `lot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const ids: string[] = [];
  for (let i = 0; i < qty; i++) {
    const ref = await addDoc(collection(db, COLLECTION), buildBottlePayload({ ...input, purchase_lot_id: lotId }));
    ids.push(ref.id);
  }
  // Single increment for the whole batch
  await updateDoc(doc(db, 'vintages', input.vintage_id), { bottle_count: increment(qty) });
  await updateDoc(doc(db, 'wines', input.wine_id), { bottle_count: increment(qty) });
  return ids;
}

// ---------------------------------------------------------------------------
// Status changes — NEVER delete, only change status
// ---------------------------------------------------------------------------

// Note: opening (consuming) a bottle is handled by services/consumption.ts —
// completeOpenBottle() — which batches the status change with the optional
// ConsumptionEvent / TastingNote / WishlistEntry writes from OpenBottleModal.

export async function changeBottleStatus(
  id: string,
  status: Exclude<BottleStatus, 'in_cellar' | 'consumed'>,
  vintageId: string
): Promise<void> {
  // The bottle leaves active inventory — keep denormalized bottle_count in sync
  // on Vintage + Wine (they are incremented on create; without this decrement the
  // stale-price cron and Investment valuations keep counting departed bottles).
  const wineId = await resolveWineIdForVintage(vintageId);
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION, id), {
    status,
    status_changed_date: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  batch.update(doc(db, 'vintages', vintageId), { bottle_count: increment(-1) });
  if (wineId) batch.update(doc(db, 'wines', wineId), { bottle_count: increment(-1) });
  await batch.commit();
}

/** Resolve the parent wine_id of a vintage (needed for counter maintenance). */
export async function resolveWineIdForVintage(vintageId: string): Promise<string | null> {
  const snap = await getDoc(doc(db, 'vintages', vintageId));
  if (!snap.exists()) return null;
  return (snap.data() as { wine_id?: string }).wine_id ?? null;
}

// ---------------------------------------------------------------------------
// Transfer (F1.6) — creates a cellar_transfers log entry
// ---------------------------------------------------------------------------

export async function transferBottle(
  bottle: Bottle,
  toCellarId: string,
  toLocationId?: string,
  reason?: string
): Promise<void> {
  // 1. Log the transfer
  await createTransfer({
    bottle_id: bottle.id,
    from_cellar_id: bottle.current_cellar_id,
    from_location_id: bottle.current_location_id,
    to_cellar_id: toCellarId,
    to_location_id: toLocationId,
    transfer_date: new Date(),
    reason,
  });

  // 2. Update bottle location
  await updateDoc(doc(db, COLLECTION, bottle.id), {
    current_cellar_id: toCellarId,
    current_location_id: toLocationId ?? null,
    updated_at: serverTimestamp(),
  });
}

// ---------------------------------------------------------------------------
// Bulk operations (F1.6 bulk) — selection-driven writes from the Inventory table
// ---------------------------------------------------------------------------

// Firestore hard-caps a WriteBatch at 500 operations. A transfer costs two
// writes per bottle (audit log + bottle patch), so 200 bottles per batch keeps
// us at 400 — safely under the cap with room for the sentinel fields.
const MAX_BOTTLES_PER_TRANSFER_BATCH = 200;
const MAX_DOCS_PER_UPDATE_BATCH = 400;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Move many bottles to a single destination cellar in one go.
 *
 * Each bottle still gets its own `cellar_transfers` audit row — the log is
 * per-bottle by design (F1.6), so a bulk move must not collapse into one entry.
 * Bottles already sitting in the destination cellar are skipped rather than
 * logged as no-op transfers.
 *
 * Returns the number of bottles actually moved.
 */
export async function transferBottles(
  bottles: Bottle[],
  toCellarId: string,
  toLocationId?: string,
  reason?: string
): Promise<number> {
  const movable = bottles.filter(b => b.current_cellar_id !== toCellarId);
  if (movable.length === 0) return 0;

  for (const group of chunk(movable, MAX_BOTTLES_PER_TRANSFER_BATCH)) {
    const batch = writeBatch(db);
    for (const bottle of group) {
      const transferRef = doc(collection(db, 'cellar_transfers'));
      batch.set(transferRef, stripUndefined({
        bottle_id: bottle.id,
        from_cellar_id: bottle.current_cellar_id,
        from_location_id: bottle.current_location_id,
        to_cellar_id: toCellarId,
        to_location_id: toLocationId,
        transfer_date: serverTimestamp(),
        reason,
      }));
      batch.update(doc(db, COLLECTION, bottle.id), {
        current_cellar_id: toCellarId,
        current_location_id: toLocationId ?? null,
        updated_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  return movable.length;
}

/**
 * Set (or clear) the special-occasion flag on many bottles at once.
 * Clearing also drops `special_occasion_reason` — a reason without a flag is
 * orphaned data that the Special Occasions dashboard would never surface.
 */
export async function setBottlesSpecialOccasion(
  bottleIds: string[],
  isSpecial: boolean
): Promise<number> {
  if (bottleIds.length === 0) return 0;

  for (const group of chunk(bottleIds, MAX_DOCS_PER_UPDATE_BATCH)) {
    const batch = writeBatch(db);
    for (const id of group) {
      batch.update(doc(db, COLLECTION, id), {
        is_special_occasion: isSpecial,
        ...(isSpecial ? {} : { special_occasion_reason: deleteField() }),
        updated_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  return bottleIds.length;
}

// ---------------------------------------------------------------------------
// Update details
// ---------------------------------------------------------------------------

export async function updateBottle(id: string, patch: BottleUpdateInput): Promise<void> {
  // Firestore rejects `undefined` values — convert them to deleteField() sentinels
  // so clearing an optional field (e.g. special_occasion_reason) actually removes it.
  const firestorePatch = Object.fromEntries(
    Object.entries(patch).map(([k, v]) => [k, v === undefined ? deleteField() : v])
  );
  await updateDoc(doc(db, COLLECTION, id), { ...firestorePatch, updated_at: serverTimestamp() });
}

/**
 * Load all bottles with a given status — used by InventoryPage for client-side join.
 * Default: 'in_cellar' (active inventory).
 */
export async function listAllBottles(status: BottleStatus = 'in_cellar'): Promise<Bottle[]> {
  // No orderBy: the inventory hook does client-side joining and sorting.
  const snap = await getDocs(query(col(), where('status', '==', status)));
  return snap.docs.map(d => d.data());
}
